import {useEffect, useState} from 'react';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

export const DEFAULT_MENTIONS_PAGE_SIZE = 50;

/**
 * Fetches paginated raw NER mention spans from GET /api/documents/entity-mentions.
 *
 * @param {Object}  options
 * @param {string}  [options.search]    - Substring match on mention_text or canonical_name
 * @param {string}  [options.entityType] - Entity type filter
 * @param {number}  [options.page]      - 0-based page index
 * @param {number}  [options.pageSize]  - Rows per page
 * @returns {{
 *   mentions: Array,
 *   loading: boolean,
 *   error: Error|null,
 *   totalElements: number,
 *   totalPages: number,
 * }}
 */
export function useEntityMentionsList({
    search = '',
    entityType = '',
    docSourceType = '',
    inDataItems = false,
    inCorpus = false,
    minNerScore,
    page = 0,
    pageSize = DEFAULT_MENTIONS_PAGE_SIZE,
} = {}) {
    const [mentions, setMentions]           = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages]       = useState(0);
    const [loading, setLoading]             = useState(true);
    const [error, setError]                 = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({page: String(page), size: String(pageSize)});
        if (search)        params.set('search', search);
        if (entityType)    params.set('type', entityType);
        if (docSourceType) params.set('docSourceType', docSourceType);
        if (inDataItems)   params.set('inDataItems', 'true');
        if (inCorpus)      params.set('inCorpus', 'true');
        // Omitting minNerScore lets the server apply its configured default (display threshold).
        if (minNerScore != null) params.set('minNerScore', String(minNerScore));

        fetch(`${BASE}/api/documents/entity-mentions?${params}`, {headers: authHeaders()})
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => {
                if (cancelled) return;
                setMentions(data.content || []);
                setTotalElements(data.totalElements ?? 0);
                setTotalPages(data.totalPages ?? 1);
            })
            .catch(err => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [search, entityType, docSourceType, inDataItems, inCorpus, minNerScore, page, pageSize]);

    return {mentions, loading, error, totalElements, totalPages};
}
